import React, { useState } from 'react';
import { getIntl } from '../../../../utils/intl';
import { MedicalDataTypes } from '../../../../primary-record-types';
import { FastField } from 'formik';
import { required } from '../../../../utils/validators';
import ImageInput from './ImageInput';
import { FormValueImage, UpdateImageValue, isUpdateValue } from './types';
import { truncate } from 'lodash';
import Stack, { ContentStack } from '../../../v2BaseComponents/Stack';
import Text from '../../../v2BaseComponents/Text';
import IconButton from '../../../v2BaseComponents/IconButton';
import { useIsDisabledByParent } from '../../../v2BaseComponents/IsDisabledProvider';
import { FormikInputs } from '../../../v2BaseComponents/Inputs';
import Divider from '../../../v2BaseComponents/Divider';
import MainContentContainer from '../../../v2BaseComponents/MainContentContainer';

const intl = getIntl('resource-images-input', {
    defaultLabel: 'Image - {order}',
    imageRequired: 'Image is required',
    description: 'Image Description',
});

export type ResourceImageInputOwnProps = {
    onClear: () => void;
    value: FormValueImage;
    index: number;
    isDisabled: boolean;
    resourceId?: string;
    profileId: string;
    category: MedicalDataTypes.PrimaryRecordResourceCategory;
    setFieldValue: (field: string, value: UpdateImageValue) => void;
};

export type ResourceImageInputProps = ResourceImageInputOwnProps;

const ResourceImageInput: React.FC<ResourceImageInputProps> = ({
    value,
    index,
    setFieldValue,
    onClear,
    isDisabled,
    resourceId,
    profileId,
    category,
}) => {
    const isDisabledByParent = useIsDisabledByParent(isDisabled);
    const [isCleared, setIsCleared] = useState(false);
    if (isCleared) return null;

    return (
        <>
            <Divider horizontal />
            <MainContentContainer>
                <ContentStack>
                    <Stack horizontal fullWidth justify="space-between" align={'center'}>
                        <Text variant="h6">
                            {truncate(value.description || intl('defaultLabel', { order: index + 1 }), {
                                length: 20,
                            })}
                        </Text>
                        <IconButton
                            onPress={() => {
                                setIsCleared(true);
                                if (isUpdateValue(value)) {
                                    // if it's an update, we need to delete the image
                                    setFieldValue(`images.${index}`, {
                                        ...value,
                                        imageValue: {
                                            ...value.imageValue,
                                            imageChange: {
                                                type: 'cleared',
                                            },
                                        },
                                    });
                                } else {
                                    // if it's new, just remove it from the list.  Creation hasn't happened
                                    onClear();
                                }
                            }}
                            variant="transparent"
                            icon="X"
                            isDisabled={isDisabledByParent}
                        />
                    </Stack>
                    <FastField
                        component={ImageInput}
                        name={`images.${index}.imageValue`}
                        resourceId={resourceId}
                        profileId={profileId}
                        category={category}
                        isDisabled={isDisabledByParent}
                        validate={(value: FormValueImage['imageValue']) => {
                            if (value?.type === 'update' && value.imageChange.type === 'cleared') {
                                return intl('imageRequired');
                            }

                            if (value?.type === 'create' && !value?.tempFileId) {
                                return intl('imageRequired');
                            }
                        }}
                    />
                    <FastField
                        component={FormikInputs.Input}
                        name={`images.${index}.description`}
                        isRequired
                        title={intl('description')}
                        isDisabled={isDisabledByParent}
                        validate={required}
                    />
                </ContentStack>
            </MainContentContainer>
        </>
    );
};

export default React.memo(ResourceImageInput);
